import React, { useState } from 'react';
import { StepContainer } from '../StepContainer';
import { LocationInput, PropertyTypeSelect, AreaInput } from '../../form';
import { useAuth } from '../../../../contexts/AuthContext';
import { findPropertyByAddress } from '../../../../services/propertyService';
import { getTVAInfoMessage } from '../../../../utils/tvaRules';
import { Info } from 'lucide-react';

interface DescriptionStepProps {
  data: any;
  onChange: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
}

interface BuildingAgeSelectorProps {
  value?: number;
  onChange: (age: number) => void;
}

const BuildingAgeSelector: React.FC<BuildingAgeSelectorProps> = ({ value, onChange }) => {
  const OPTIONS = [
    { age: 1, label: 'Moins de 2 ans', sub: 'TVA 20%' },
    { age: 2, label: 'Plus de 2 ans', sub: 'TVA réduite possible' },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <label style={{
        fontSize: 11, fontWeight: 500, color: '#7C6FA0',
        textTransform: 'uppercase', letterSpacing: '0.06em',
        marginBottom: 8, display: 'block',
      }}>
        Âge du logement
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        {OPTIONS.map(({ age, label, sub }) => {
          const selected = value !== undefined && (age === 1 ? value < 2 : value >= 2);
          return (
            <button
              key={age}
              type="button"
              onClick={() => onChange(age)}
              style={{
                flex: 1,
                padding: '10px 14px',
                borderRadius: 'var(--radius-md)',
                border: selected ? '0.5px solid rgba(109,40,217,0.35)' : '0.5px solid var(--color-border)',
                background: selected ? '#F5F3FF' : 'white',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: selected ? 500 : 400, color: selected ? '#4C1D95' : '#1E1148' }}>
                {label}
              </div>
              <div style={{ fontSize: 11, color: selected ? '#6D28D9' : '#7C6FA0', marginTop: 2 }}>
                {sub}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const DescriptionStep: React.FC<DescriptionStepProps> = ({
  data = {
    location: { postcode: '', city: '', address: '' },
    propertyType: null,
    area: 0
  },
  onChange,
  onNext,
}) => {
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const isValid = Boolean(
    data?.location?.address &&
    data?.location?.postcode &&
    data?.propertyType &&
    data?.area &&
    data?.area > 0 &&
    data?.buildingAge !== undefined
  );

  const handleNext = async () => {
    if (!user) return;
    try {
      setLoading(true);
      setError(null);
      if (!data?.location?.address || !data?.location?.postcode || !data?.propertyType) {
        setError("Veuillez remplir l'adresse complète");
        return;
      }
      const existingProperty = await findPropertyByAddress(user.uid, data.location.address, data.location.postcode);
      if (existingProperty?.estimateId) {
        setError('Une estimation existe déjà pour ce bien.');
        return;
      }
      onNext();
    } catch {
      setError('Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <StepContainer
      title="Description du bien"
      description="Localisation, type et caractéristiques du bien à rénover"
      onNext={handleNext}
      isValid={isValid && !loading}
    >
      <LocationInput
        value={data.location}
        onChange={(location) => onChange({ ...data, location })}
      />

      <PropertyTypeSelect
        value={data.propertyType}
        onChange={(type) => onChange({ ...data, propertyType: type })}
      />

      <AreaInput
        value={data.area || 0}
        onChange={(area) => onChange({ ...data, area })}
      />

      <BuildingAgeSelector
        value={data.buildingAge}
        onChange={(buildingAge) => onChange({ ...data, buildingAge })}
      />

      {data.buildingAge !== undefined && (
        <div
          className="flex items-start gap-2"
          style={{
            padding: '10px 14px',
            background: '#EFF6FF',
            border: '0.5px solid rgba(59,130,246,0.2)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <Info size={13} color="#3B82F6" style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 12, color: '#1D4ED8', lineHeight: 1.5 }}>
            {getTVAInfoMessage(data.buildingAge)}
          </p>
        </div>
      )}

      {error && (
        <div style={{
          padding: '10px 14px',
          background: '#FEF2F2',
          border: '0.5px solid rgba(220,38,38,0.2)',
          borderRadius: 'var(--radius-md)',
          fontSize: 12,
          color: '#DC2626',
        }}>
          {error}
        </div>
      )}
    </StepContainer>
  );
};

export default DescriptionStep;
